/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { ILliCheckoutAdditionalInfoData } from './lli-checkout-additional-info.data';
import { ILliCheckoutAdditionalInfoProps } from './lli-checkout-additional-info.props.autogenerated';

import getAddAdditionalInfo, { IAddAdditionalInfo } from './components/get-add-additional-info';
import getShowAdditionalInfo, { IShowAdditionalInfo } from './components/get-show-additional-info';
import { AttributeTextValue, AttributeValueBase } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

export * from './components/get-add-additional-info';
export * from './components/get-show-additional-info';

interface ICheckoutAdditionalInfoState {
    orderReference: string;
    orderNotes: string;
    requestedShippingDate: Date | null; // stored as ISO string in attribute, this also means "T07" indicates timezone
}

export interface ICheckoutAdditionalInfoModuleProps extends ILliCheckoutAdditionalInfoProps<ILliCheckoutAdditionalInfoData>, IModuleStateProps { }

export interface ILliCheckoutAdditionalInfoViewProps extends ILliCheckoutAdditionalInfoProps<ILliCheckoutAdditionalInfoData>, ICheckoutAdditionalInfoState {
    moduleProps: IModuleProps;
    showAdditionalInfo?: IShowAdditionalInfo;
    addAdditionalInfo?: IAddAdditionalInfo;

    onOrderReferenceChange?(orderReference: string): void;
    onOrderNotesChange?(orderNotes: string): void;
    onRequestedShippingDateChange?(requestedShippingDate: Date): void;

    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * LliCheckoutAdditionalInfo component
 * @extends {React.PureComponent<ILliCheckoutAdditionalInfoProps<ILliCheckoutAdditionalInfoData>, ICheckoutAdditionalInfoState>}
 */
// @ts-expect-error
@withModuleState
@observer
class LliCheckoutAdditionalInfo extends React.PureComponent<ICheckoutAdditionalInfoModuleProps, ICheckoutAdditionalInfoState> {
    public state: ICheckoutAdditionalInfoState = {
        orderReference: '',
        orderNotes: '',
        requestedShippingDate: null
    };

    private readonly orderReferenceRef: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly orderNotesRef: React.RefObject<HTMLInputElement> = React.createRef();
    
    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );
    }

    public shouldComponentUpdate(nextProps: ICheckoutAdditionalInfoModuleProps,
        nextState: ICheckoutAdditionalInfoState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasError, hasExternalSubmitGroup }
        } = this.props;
        const orderReference = this.getTextAttribute("OrderReference") ?? this.state.orderReference;
        const orderNotes = this.getTextAttribute("Notes") ?? this.state.orderNotes;
        const requestedShippingDateText = this.getTextAttribute("RequestedshipDate") || this.dateToString(this.state.requestedShippingDate);
        const requestedShippingDate = (requestedShippingDateText && new Date(requestedShippingDateText)) || null;

        const viewProps: ILliCheckoutAdditionalInfoViewProps = {
            ...this.props,
            ...this.state,
            onOrderReferenceChange: this.onOrderReferenceChange,
            onOrderNotesChange: this.onOrderNotesChange,
            onRequestedShippingDateChange: this.onRequestedShippingDateChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('lli-checkout-additional-info', className)
            },
            showAdditionalInfo: isReady ? getShowAdditionalInfo({
                canEdit: !hasExternalSubmitGroup,
                orderReference,
                orderNotes,
                requestedShippingDate,
                resources,
                onEdit: this.onEdit
            }) : undefined,
            addAdditionalInfo: !isReady ? getAddAdditionalInfo({
                orderReference: this.state.orderReference,
                orderNotes: this.state.orderNotes,
                requestedShippingDate: this.state.requestedShippingDate,
                hasError,
                resources,
                orderReferenceRef: this.orderReferenceRef,
                orderNotesRef: this.orderNotesRef,
                onOrderReferenceChange: this.onOrderReferenceChange,
                onOrderNotesChange: this.onOrderNotesChange,
                onRequestedShippingDateChange: this.onRequestedShippingDateChange,
                canSubmit: !hasExternalSubmitGroup,
                onSubmit: this.onSubmit,
                canCancel: !hasExternalSubmitGroup && !!orderReference,
                onCancel: this.onCancel
            }) : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const orderReference = this.getTextAttribute("OrderReference") || this.state.orderReference;
        const orderNotes = this.getTextAttribute("Notes") || this.state.orderNotes;
        const requestedShippingDate = this.getTextAttribute("RequestedshipDate") || this.dateToString(this.state.requestedShippingDate) || "";
        
        await this.updateTextAttribute([
            {name: "OrderReference", value: orderReference},
            {name: "Notes", value: orderNotes},
            {name: "RequestedshipDate", value: requestedShippingDate}
        ]);

        this.setState({ orderReference, orderNotes, requestedShippingDate: (requestedShippingDate && new Date(requestedShippingDate)) || null });
        
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: !!orderReference ? 'ready' : 'updating',
            hasError: false // false so error won't show on init
        });
    };

    private readonly onOrderReferenceChange = (orderReference: string): void => {
        this.setState({ orderReference });
        if (this.props.moduleState.hasError) {
            // Clear error user updates order reference
            this.props.moduleState.setHasError(false);
        }
    };

    private readonly onOrderNotesChange = (orderNotes: string): void => {
        this.setState({ orderNotes });
    };

    private readonly onRequestedShippingDateChange = (requestedShippingDate: Date | null): void => {
        this.setState({ requestedShippingDate });
    }

    private readonly isOrderReferenceValid = (orderReference: string): boolean => {
        return !!orderReference;
    }

    private readonly onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        const { orderReference, orderNotes, requestedShippingDate } = this.state;
        
        const isValid = this.isOrderReferenceValid(orderReference);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            // eslint-disable-next-line xss/no-mixed-html -- Get error, focus back on the input field.
            const input = this.orderReferenceRef.current?.focus && (this.orderReferenceRef.current as HTMLElement);
            input?.focus();
        } else {
            this.props.moduleState.onReady();
            await this.updateTextAttribute([
                {name: "OrderReference", value: orderReference},
                {name: "Notes", value: orderNotes},
                {name: "RequestedshipDate", value: this.dateToString(requestedShippingDate) || ""}
            ]);
    
            this.setState({ orderReference, orderNotes, requestedShippingDate });
        }
    };

    private readonly onCancel = (): void => {
        const orderReference = this.getTextAttribute("OrderReference") || "";
        const orderNotes = this.getTextAttribute("Notes") || "";
        const requestedShippingDateText = this.getTextAttribute("RequestedshipDate") || "";
        const requestedShippingDate = (requestedShippingDateText && new Date(requestedShippingDateText)) || null;

        if (!orderReference) {
            // It has no saved order reference, set to status updating and request user to fill the order reference form
            this.props.moduleState.onUpdating();
            
        } else {
            // It has saved order reference, set to status ready
            this.setState({
                orderReference,
                orderNotes,
                requestedShippingDate
            });
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
    };

    private readonly updateTextAttribute = async (args: {name: string, value: string}[]): Promise<void> => {
        const cartState = this.props.data.checkout.result?.checkoutCart;

        let newAttributeValues:AttributeValueBase[] = cartState?.attributeValues || [];

        for (var newAttr of args) {
            newAttributeValues = newAttributeValues.filter(attr => attr.Name !== newAttr.name);
            newAttributeValues.push({
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: newAttr.name,
                ExtensionProperties: [],
                TextValue: newAttr.value,
                TextValueTranslations: []
            });
        }

        await cartState?.updateAttributeValues({newAttributeValues});
    };

    private readonly getTextAttribute = (attributeName: string): string | undefined => {
        const attributeOnCart = this.props.data.checkout.result?.checkoutCart.attributeValues.find(attr => attr.Name === attributeName) as AttributeTextValue;
        const val = attributeOnCart?.TextValue;
        return val;
    }

    private readonly dateToString = (date: Date | null) => {
        if (!date) return "";
        let year = date.getFullYear();
        let month = (1 + date.getMonth()).toString().padStart(2, '0');
        let day = date.getDate().toString().padStart(2, '0');
      
        return month + '/' + day + '/' + year;
    }
}

export default LliCheckoutAdditionalInfo;
